import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  schema?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  schema,
}: SEOHeadProps) {
  const siteName = '原神本子精选';
  const fullTitle = `${title} — ${siteName}`;
  const baseUrl = 'https://artsygen.github.io';

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    const canonicalHref = canonicalUrl || baseUrl;
    if (canonicalEl) {
      canonicalEl.setAttribute('href', canonicalHref);
    } else {
      const link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', canonicalHref);
      document.head.appendChild(link);
    }

    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalHref, true);
    setMeta('og:site_name', siteName, true);
    setMeta('og:locale', 'zh_CN', true);
    if (ogImage) setMeta('og:image', ogImage, true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    if (schema) {
      const existingSchema = document.querySelector('script[type="application/ld+json"][data-dynamic]');
      if (existingSchema) existingSchema.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-dynamic', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      const dynamicSchema = document.querySelector('script[type="application/ld+json"][data-dynamic]');
      if (dynamicSchema) dynamicSchema.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, fullTitle, schema]);

  return null;
}